import React, { ReactNode } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { t } from 'Src/i18n';
import { Icon } from 'Components/frameworks/icon/Icon';
import { ICONS } from 'Components/utils/icon/icons';
import { getPackage } from 'Utils/proposal/proposalUtils';
import { PACKAGE_FOOD_AND_BEVERAGE } from 'Utils/proposal/constants';
import { SellerCommission } from 'Views/booking/receipt/SellerCommission';
import { tAlt } from 'Components/utils/booking/bookingUtils';
import { PaidServicesListItem } from './PaidServicesListItem';
import PaidServicesTransportDetails from './PaidServicesTransportDetails';
import { PAID_SERVICE } from './paidService.constants';
const SUBSCRIPTION_FEE_TYPE = 'SUBSCRIPTION';
const PORT_TAXES_FEE_TYPE = 'PORT_TAXES';
type LineSubItem = {
  title: string;
  price: string;
};
export type LineItem =
  | {
      type: 'package';
      readonly price: string;
      readonly accommodationTitle: string;
    }
  | {
      type: 'room';
      readonly accommodationTitle: string;
    }
  | {
      type: 'transport';
      readonly transports: Transport;
    }
  | {
      type: 'discount';
      readonly offerName: string;
      readonly discount: string;
      readonly details: ReactNode;
    }
  | {
      type: 'allInclusive';
      readonly subItems: Array<LineSubItem>;
    }
  | {
      type: 'transfer';
      // no price means it's included
      readonly price: string;
      readonly details: string | null | undefined;
    }
  | {
      type: 'childcares';
      readonly price: string;
      readonly title: string;
      readonly details: ReactNode;
    }
  | {
      type: 'skipass';
    }
  | {
      type: 'breakfast';
    }
  | {
      type: 'ffb';
      readonly subItems: Array<LineSubItem>;
    }
  | {
      type: 'fees';
      readonly feeType: string;
      readonly price: string;
    }
  | {
      readonly type: 'donation';
      readonly value: string;
    }
  | {
      readonly type: 'promoCode';
      readonly value: string;
    }
  | {
      readonly type: 'bestRate';
      readonly value: string;
    }
  | {
      readonly type: 'insurance';
      readonly value: string;
      readonly price: string;
      readonly details: ReactNode;
    }
  | {
      readonly type: 'rental';
      readonly price: string;
    }
  | {
      readonly type: 'excursions';
      readonly price: string;
    }
  | {
      readonly type: 'wellness';
      readonly price: string;
    }
  | {
      readonly type: 'sports';
      readonly price: string;
    }
  | {
      readonly type: 'totalPrice';
      readonly totalPrice: FormattedPrice;
      readonly label: string;
    }
  | {
      readonly type: 'deposit';
      readonly depositAmount?: string;
      readonly label?: string;
    };
type Props = {
  proposal: Proposal;
  items: Array<LineItem>;
  isHomeDetails?: boolean;
  i18n: any;
  // base i18n
  resort: Product;
  dataTestId?: string;
};

const PaidServicesListItemWrapper = ({
  item,
  index,
  i18n,
  isHomeDetails,
  proposal,
  resort,
}: {
  item: LineItem;
  index: number;
  proposal: Proposal;
  isHomeDetails?: boolean;
  i18n: any;
  // base i18
  resort: Product;
}) => {
  const { priceDetails } = i18n.booking;

  switch (item.type) {
    case PAID_SERVICE.PACKAGE:
      return (
        <PaidServicesListItem
          isHomeDetails={isHomeDetails}
          key={index}
          index={index}
          title={priceDetails.holidayPackage}
          price={item.price}
        />
      );

    case PAID_SERVICE.ROOM:
      return (
        <PaidServicesListItem
          isHomeDetails={isHomeDetails}
          key={index}
          index={index}
          title={tAlt({
            cruiseKeyPath: 'booking.priceDetails.packagePrice.cabin',
            product: resort,
            resortKeyPath: 'booking.priceDetails.packagePrice.room',
          })}
          price={priceDetails.packagePrice.included}
          details={item.accommodationTitle}
        />
      );

    case PAID_SERVICE.BEST_RATE:
      return (
        <div
          key={index}
          className={classnames(
            'CollapsibleItem flex justify-center gap-10',
            {
              'py-10': isHomeDetails,
            },
            {
              'py-15': !isHomeDetails,
            },
          )}
        >
          <span className="font-bold">{item.value}</span>
          <div
            className="inline-flex size-22 items-center justify-center rounded-full bg-saffron text-black"
            data-testid="PaidServicesListItem-bestRate"
          >
            <Icon name={ICONS.CHECK} />
          </div>
        </div>
      );

    case PAID_SERVICE.DISCOUNT:
      return (
        <PaidServicesListItem
          isHomeDetails={isHomeDetails}
          key={index}
          index={index}
          price={item.discount}
          title={item.offerName}
          titleClassName="font-bold"
          color="text-[#924833]"
          details={item.details}
        />
      );

    case PAID_SERVICE.TRANSFER:
      return (
        <PaidServicesListItem
          isHomeDetails={isHomeDetails}
          key={index}
          index={index}
          price={item.price || priceDetails.packagePrice.included}
          title={priceDetails.packagePrice.transfer}
          details={item.details}
        />
      );

    case PAID_SERVICE.CHILDCARES:
      return (
        <PaidServicesListItem
          isHomeDetails={isHomeDetails}
          key={index}
          index={index}
          title={item.title}
          price={item.price}
          details={item.details}
        />
      );

    case PAID_SERVICE.TRANSPORT:
      return (
        <PaidServicesListItem
          isHomeDetails={isHomeDetails}
          key={index}
          index={index}
          title={priceDetails.packagePrice.transport}
          price={priceDetails.packagePrice.included}
          details={<PaidServicesTransportDetails i18n={i18n} transports={item.transports} />}
        />
      );

    case PAID_SERVICE.SKIPASS:
      return (
        <PaidServicesListItem
          isHomeDetails={isHomeDetails}
          key={index}
          index={index}
          title={priceDetails.packagePrice.skiPassIncluded}
          price={priceDetails.packagePrice.included}
          details={
            <ul>
              <li>{priceDetails.packagePrice.skiPassInfo}</li>
              <li>{priceDetails.packagePrice.skiLessonsInfo}</li>
            </ul>
          }
        />
      );

    case PAID_SERVICE.ALL_INCLUSIVE:
      return (
        <PaidServicesListItem
          isHomeDetails={isHomeDetails}
          key={index}
          index={index}
          title={priceDetails.packagePrice.allInclusive}
          subItems={item.subItems}
          icon={ICONS.ALL_INCLUSIVE}
          color="text-[#005589]"
          titleClassName="font-brand"
          details={priceDetails.packagePrice.allInclusiveReassurance.description}
        />
      );

    case PAID_SERVICE.BREAKFAST:
      return (
        <PaidServicesListItem
          isHomeDetails={isHomeDetails}
          key={index}
          index={index}
          title={priceDetails.packagePrice.breakfastIncludedRon}
          price={priceDetails.packagePrice.included}
          details={priceDetails.packagePrice.joyview.breakfastIncluded}
        />
      );

    case PAID_SERVICE.FFB:
      return (
        <PaidServicesListItem
          isHomeDetails={isHomeDetails}
          key={index}
          index={index}
          title={getPackage(proposal, PACKAGE_FOOD_AND_BEVERAGE).label}
          subItems={item.subItems}
          icon={ICONS.DISHES}
          color="text-[#cf4a2a]"
          titleClassName="font-brand"
          details={priceDetails.packagePrice.allInclusiveReassurance.description}
        />
      );

    case PAID_SERVICE.INSURANCE:
      return (
        <PaidServicesListItem
          isHomeDetails={isHomeDetails}
          key={index}
          index={index}
          title={item.value}
          price={item.price}
          details={item.details}
        />
      );

    case PAID_SERVICE.PROMO_CODE:
      return (
        <PaidServicesListItem
          isHomeDetails={isHomeDetails}
          key={index}
          index={index}
          title={priceDetails.promoCode.label || priceDetails.packagePrice.reduction}
          price={item.value}
        />
      );

    case PAID_SERVICE.FEES:
      let title;
      let details;

      if (item.feeType === SUBSCRIPTION_FEE_TYPE) {
        title = priceDetails.packagePrice.fees.annualMembershipFeesLabel;
        details = priceDetails.packagePrice.annualFeesInformation;
      } else if (item.feeType === PORT_TAXES_FEE_TYPE) {
        title = priceDetails.packagePrice.fees.portTaxesLabel;
        details = priceDetails.packagePrice.fees.portTaxesDescription;
      } else {
        title = priceDetails.packagePrice.fees.touristicTaxesLabel;
        details = priceDetails.packagePrice.fees.touristicTaxesDescription;
      }

      return (
        <PaidServicesListItem
          isHomeDetails={isHomeDetails}
          key={index}
          index={index}
          title={title}
          details={details}
          price={item.price}
        />
      );

    case PAID_SERVICE.RENTAL:
      return (
        <PaidServicesListItem
          isHomeDetails={isHomeDetails}
          key={index}
          index={index}
          title={priceDetails.packagePrice.skiEquipment}
          price={item.price}
        />
      );

    case PAID_SERVICE.EXCURSIONS:
      return (
        <PaidServicesListItem
          isHomeDetails={isHomeDetails}
          key={index}
          index={index}
          title={priceDetails.packagePrice.excursions}
          price={item.price}
        />
      );

    case PAID_SERVICE.SPORTS:
      return (
        <PaidServicesListItem
          isHomeDetails={isHomeDetails}
          index={index}
          key={`${item.type}-${index}`}
          title={priceDetails.packagePrice.sportActivities}
          price={item.price}
        />
      );

    case PAID_SERVICE.WELLNESS:
      return (
        <PaidServicesListItem
          isHomeDetails={isHomeDetails}
          key={index}
          index={index}
          title={priceDetails.packagePrice.wellnessActivities}
          price={item.price}
        />
      );

    case PAID_SERVICE.DONATION:
      return (
        <PaidServicesListItem
          isHomeDetails={isHomeDetails}
          key={index}
          index={index}
          title={priceDetails.packagePrice.donation}
          price={item.value}
          details={priceDetails.packagePrice.donationInformation}
        />
      );

    case PAID_SERVICE.TOTAL_PRICE:
      const { totalPrice, label } = item;
      return (
        <div
          className="-mx-15 flex flex-col border-t border-lightGrey px-15 py-20"
          key={index}
          data-testid="PaidServicesListItem-totalPrice"
        >
          <div className="flex w-full items-center justify-between text-orange">
            <span className="font-bold">{label}</span>
            <div className="flex">
              <span className="font-bold md:text-20">{totalPrice.formattedValue}</span>
            </div>
          </div>
          {i18n.booking.priceDetails.packagePrice.fees.displayTaxesAndFeesIncludedMention && (
            <div className="text-12 text-mediumGrey">
              {t('booking.priceDetails.packagePrice.fees.taxesAndFeesIncludedLabel')}
            </div>
          )}
        </div>
      );

    // deposit only used in b2b
    case PAID_SERVICE.DEPOSIT:
      return null;

    default: {
      const unreachable: LineItem = item;
      throw new Error(
        `PaidServicesList cannot render items of type ${(unreachable as { type: string }).type}`,
      );
    }
  }
};

export const PaidServicesList = ({
  i18n,
  isHomeDetails = false,
  proposal,
  items,
  resort,
  dataTestId = '',
}: Props) => {
  return (
    <div dat-testid={dataTestId}>
      {items.map((item, index) => (
        <PaidServicesListItemWrapper
          key={`PaidServicesListItemWrapper-${index + dataTestId}`}
          i18n={i18n}
          isHomeDetails={isHomeDetails}
          proposal={proposal}
          resort={resort}
          item={item}
          index={index}
        />
      ))}
      {proposal.sellerCommission && <SellerCommission proposal={proposal} />}
    </div>
  );
};
